import { type ReactElement, useState } from "react"

import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { useInfiniteQuery } from "react-query"
import { useSelector } from "react-redux"

import type { AxiosError, AxiosResponse } from "axios"

import { AutoMLCreationDialog, CreationDialog } from "../../../features/Models"
import { KonanAPI } from "../../../services/KonanAPI"
import type { RootState } from "../../../store/ReduxStore"
import type { ModelListCreate } from "../../../types/generated/api/ModelListCreate"
import type { PaginatedModelListCreateList } from "../../../types/generated/api/PaginatedModelListCreateList"
import { extractPageFromBackEndPaginationLink } from "../../../utils/genericHelpers"
import { ModelsList } from "./components/ModelsList/ModelsList"
import { TopSection } from "./components/TopSection/TopSection"

export const ModelsSidebarContainer = (): ReactElement => {
  const { t } = useTranslation("models")
  const currentProject = useSelector((state: RootState) => state.project.currentProject)

  // Configure intersection observer for infinite scroll functionality
  // Only triggers next page fetch when last item is in view and more data is available
  const { ref: lastItemRef } = useInView({
    onChange: (inView: boolean) => {
      // Fetch next page only when: element is in view AND has more pages AND not currently fetching
      inView && hasNextPage && !isModelsFetching && !isFetchingNextPage && fetchNextPage()
    },
  })

  const isAutoMLProject = currentProject?.type === "credit_scoring"

  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)

  const {
    data: models,
    isFetching: isModelsFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery<AxiosResponse<PaginatedModelListCreateList>, AxiosError, ModelListCreate>(
    ["models", currentProject?.uuid, "paginated", "state"],
    ({ pageParam = 1 }) =>
      KonanAPI.fetchModels({
        projectId: currentProject?.uuid as string,
        page: pageParam,
        ordering: "state",
      }),
    {
      select: ({ pageParams, pages }) => ({
        pageParams: pageParams,
        pages: pages.reduce<ModelListCreate[]>(
          (accumulator, page) => accumulator.concat(page.data.results as ModelListCreate[]),
          [],
        ),
      }),
      getNextPageParam: (lastPage) => {
        return lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data?.next) : false
      },
      refetchOnMount: true,
      enabled: !!currentProject?.uuid,
    },
  )

  return (
    <div>
      {isDialogOpen &&
        (isAutoMLProject ? (
          <AutoMLCreationDialog isOpen setIsOpen={setIsDialogOpen} />
        ) : (
          <CreationDialog isOpen onClose={() => setIsDialogOpen(false)} />
        ))}

      <TopSection
        buttonTitle={isAutoMLProject ? t("sidebar.buttonTitles.trainNewModel") : t("sidebar.buttonTitles.newModel")}
        projectName={currentProject?.name as string}
        setOpenNewModelDialog={setIsDialogOpen}
      />
      <ModelsList
        modelsArr={models?.pages}
        isModelsFetching={isModelsFetching}
        lastItemRef={lastItemRef}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    </div>
  )
}
